// Fullscreen sandbox wrapper — the ScoringEngine handles everything.
// User can edit team/player names inline; localStorage persists across refresh.
import { useState } from "react";
import { Link } from "react-router-dom";
import ScoringEngine, { buildDefaultGame } from "@/components/ScoringEngine";
import { ArrowLeft, Pencil, Save } from "lucide-react";

export default function Sandbox() {
  // We render the engine in a `key` cycle so "Edit Rosters" can re-mount it cleanly
  // after the user customises names. Pre-edit values are written straight to localStorage.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => {
    try {
      const raw = localStorage.getItem("hm_sandbox_game");
      if (raw) return JSON.parse(raw);
    } catch (err) {
      console.warn("[sandbox] failed to parse cached game, starting fresh", err);
    }
    return buildDefaultGame();
  });
  const [version, setVersion] = useState(0);

  const saveDraft = () => {
    localStorage.setItem("hm_sandbox_game", JSON.stringify(draft));
    setEditing(false);
    setVersion(v => v + 1); // remount the engine with the new roster
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="bg-charcoal border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" data-testid="sandbox-exit-btn" className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-zinc-400 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> Exit Sandbox
          </Link>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Sandbox · stored locally · no account required
          </div>
          <button
            data-testid="sandbox-edit-rosters-btn"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-court hover:text-white"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit Rosters
          </button>
        </div>
      </div>

      {editing ? (
        <div className="max-w-5xl mx-auto px-6 py-10">
          <h2 className="font-display font-black text-3xl uppercase tracking-tight">Customise Game</h2>
          <p className="text-sm text-zinc-400 mt-2">Edit team and player names below. All stats are kept in your browser only.</p>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {["teamA", "teamB"].map(key => (
              <div key={key} className="border border-zinc-800 p-5">
                <label className="block">
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">{key === "teamA" ? "Team A" : "Team B"} Name</span>
                  <input
                    data-testid={`edit-${key}-name`}
                    value={draft[key].name}
                    onChange={(e) => setDraft({ ...draft, [key]: { ...draft[key], name: e.target.value } })}
                    className="w-full mt-2 bg-transparent border border-zinc-800 focus:border-court py-2 px-3 outline-none text-white"
                  />
                </label>
                <div className="mt-4 space-y-2">
                  {draft[key].players.map((p, i) => (
                    <div key={p.id} className="flex gap-2 items-center">
                      <input
                        data-testid={`edit-${key}-player-${i}-number`}
                        type="number"
                        value={p.number}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          const players = [...draft[key].players];
                          players[i] = { ...p, number: v };
                          setDraft({ ...draft, [key]: { ...draft[key], players } });
                        }}
                        className="w-16 bg-transparent border border-zinc-800 focus:border-court py-1.5 px-2 outline-none text-white font-mono text-sm"
                      />
                      <input
                        data-testid={`edit-${key}-player-${i}-name`}
                        value={p.name}
                        onChange={(e) => {
                          const players = [...draft[key].players];
                          players[i] = { ...p, name: e.target.value };
                          setDraft({ ...draft, [key]: { ...draft[key], players } });
                        }}
                        className="flex-1 bg-transparent border border-zinc-800 focus:border-court py-1.5 px-2 outline-none text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <button
              data-testid="edit-save-btn"
              onClick={saveDraft}
              className="h-12 px-6 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 inline-flex items-center gap-2"
            ><Save className="w-4 h-4" /> Save & Tip Off</button>
            <button onClick={() => setEditing(false)} className="h-12 px-6 border border-zinc-800 text-zinc-400 hover:text-white text-xs font-bold tracking-widest uppercase">Cancel</button>
          </div>
        </div>
      ) : (
        <ScoringEngine key={version} />
      )}
    </div>
  );
}
